import React, { FC, ReactNode, useRef } from "react";
import Loading from "./Loading";
import { Button as MuiButton } from "@mui/material";
import { styled } from "@mui/system";
import {
  COLORS,
  FONTS,
  FONT_FAMILIES,
  FONT_WEIGHTS,
  RADII,
  SIZES,
  SPACINGS,
} from "../../common/consts";
import { makeShouldForwardProps } from "../../common/utils/ui";
import Text, { TextVariant } from "./Text";
import SaveButton from "./SaveButton";
import Spinner from "./Spinner";

export enum ButtonVariants {
  brand = "brand",
  light = "light",
  pill = "pill",
  text = "text",
}

export enum ButtonSizes {
  sm = "sm",
  md = "md",
  lg = "lg",
}

const getMuiSize = (size: ButtonSizes): "small" | "medium" | "large" => {
  switch (size) {
    case ButtonSizes.sm:
      return "small";
    case ButtonSizes.md:
      return "medium";
    case ButtonSizes.lg:
      return "large";
    default:
      return "medium";
  }
};

const getTextSize = (size: ButtonSizes): TextVariant => {
  switch (size) {
    case ButtonSizes.sm:
      return TextVariant.sm;
    case ButtonSizes.md:
      return TextVariant.md;
    case ButtonSizes.lg:
      return TextVariant.lg;
    default:
      return TextVariant.sm;
  }
};

interface CustomMuiButtonProps {
  active?: boolean;
}

const shouldForwardProp = makeShouldForwardProps<CustomMuiButtonProps>([
  "active",
]);

const CustomMuiButton = styled(MuiButton, {
  shouldForwardProp,
})<CustomMuiButtonProps>`
  && {
    /* Outreach's pill, measured: 36px tall, 12px sides, 64px minimum, 14px/600. */
    font: ${FONTS.sm};
    font-weight: 600;
    text-transform: none;
    border-radius: ${RADII.full};
    height: 36px;
    padding: 0 12px;
    min-width: 64px;
    box-shadow: none;
    color: ${({ active }) =>
      active
        ? `var(--color-foregroundSecondary, var(--color-foregroundSecondaryLight, ${COLORS.foregroundSecondaryLight}))`
        : `var(--color-foregroundPrimary, ${COLORS.foregroundPrimary})`};
  }
  &:hover {
    background-color: var(--color-backgroundSecondary, ${COLORS.backgroundSecondary});
  }
`;

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color"> {
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  loading?: boolean;
  variant?: ButtonVariants;
  size?: ButtonSizes;
  fullWidth?: boolean;
  active?: boolean;
}

const Button: FC<ButtonProps> = ({
  children,
  className = "",
  startIcon,
  endIcon,
  loading,
  variant = ButtonVariants.brand,
  size = ButtonSizes.sm,
  fullWidth = false,
  active,
  type,
  ...rest
}) => {
  const isPillVariant = variant === ButtonVariants.pill;
  // Use SaveButton styling for brand variant buttons when loading or when it's a submit Save button
  const isSaveButton =
    type === "submit" &&
    variant === ButtonVariants.brand &&
    (children === "Save" || (typeof children === "string" && children.trim() === "Save"));
  
  // Use SaveButton component for brand variant buttons with loading state
  // This ensures consistent loading appearance across all brand buttons
  const useSaveButtonComponent = variant === ButtonVariants.brand && loading;

  // Use SaveButton component for save buttons or brand buttons with loading
  if (isSaveButton || useSaveButtonComponent) {
    return (
      <SaveButton
        loading={loading}
        disabled={rest.disabled}
        className={className}
        type={type}
        {...rest}
      >
        {children}
      </SaveButton>
    );
  }

  if (isPillVariant) {
    return (
      <CustomMuiButton
        fullWidth={fullWidth}
        className={className}
        size={getMuiSize(size)}
        startIcon={loading ? <Loading /> : startIcon}
        endIcon={loading ? undefined : endIcon}
        active={active}
        type={type}
        {...rest}
      >
        {loading ? null : children}
      </CustomMuiButton>
    );
  }

  const buttonClassName = `btn btn-${variant} btn-${size} ${active ? "active" : ""} ${
    fullWidth ? "w-100" : ""
  } ${className}`;

  // For brand variant buttons, use the SaveButton spinner style
  const useSaveSpinner = variant === ButtonVariants.brand && loading;

  return (
    <button className={buttonClassName} type={type} {...rest}>
      <div className="d-flex gap-2" style={{ alignItems: "center" }}>
        {loading ? (
          useSaveSpinner ? (
            <Spinner />
          ) : (
            <Loading />
          )
        ) : (
          <>
            {startIcon && <div style={{ display: "flex", alignItems: "center" }}>{startIcon}</div>}
            <Text 
              variant={getTextSize(size)} 
              color="inherit" 
              style={{ 
                display: "flex", 
                alignItems: "center",
                ...(variant === ButtonVariants.brand ? { fontWeight: 575 } : {})
              }}
            >
              {children}
            </Text>
            {endIcon && <div style={{ display: "flex", alignItems: "center" }}>{endIcon}</div>}
          </>
        )}
      </div>
    </button>
  );
};

export default Button;
